package org.chiangmeng.qfast.core.ui.staff

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateContentSize
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.DeleteForever
import androidx.compose.material.icons.filled.QrCode
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material.icons.filled.SkipNext
import androidx.compose.material.icons.filled.VolumeUp
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import io.github.alexzhirkevich.qrose.rememberQrCodePainter
import org.chiangmeng.qfast.core.ui.components.FloatingCard

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun StaffDashboardScreen(
    state: StaffContract.State,
    onIntent: (StaffContract.Intent) -> Unit,
    bottomAdSlot: @Composable () -> Unit = {}
) {
    val navigator = LocalNavigator.currentOrThrow
    var showQrDialog by remember { mutableStateOf(false) }

    Scaffold(
        topBar = {
            CenterAlignedTopAppBar(
                title = { Text("QFast Staff Admin", fontWeight = FontWeight.Bold) },
                actions = {
                    IconButton(onClick = { showQrDialog = true }) {
                        Icon(
                            imageVector = Icons.Default.QrCode,
                            contentDescription = "Shop QR Code"
                        )
                    }
                    IconButton(onClick = { navigator.push(SettingsScreen(state = state, onIntent = onIntent)) }) {
                        Icon(Icons.Default.Settings, contentDescription = "Settings")
                    }
                }
            )
        },
        bottomBar = {
            Box(modifier = Modifier.fillMaxWidth().heightIn(min = 50.dp)) {
                bottomAdSlot()
            }
        }
    ) { paddingValues ->
        if (state.isLoading) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        } else {
            BoxWithConstraints(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(paddingValues)
            ) {
                Column(modifier = Modifier.fillMaxSize()) {
                    state.errorMessage?.let { errorMsg ->
                        Card(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 16.dp, vertical = 8.dp),
                            colors = CardDefaults.cardColors(
                                containerColor = MaterialTheme.colorScheme.errorContainer
                            )
                        ) {
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(12.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Warning,
                                    contentDescription = "Error",
                                    tint = MaterialTheme.colorScheme.error
                                )
                                Text(
                                    text = "ข้อผิดพลาด: $errorMsg",
                                    color = MaterialTheme.colorScheme.onErrorContainer,
                                    fontSize = 13.sp,
                                    modifier = Modifier.weight(1f)
                                )
                            }
                        }
                    }

                    if (this@BoxWithConstraints.maxWidth > 600.dp) {
                        Row(modifier = Modifier.fillMaxSize()) {
                            Box(modifier = Modifier.weight(1f)) {
                                StaffControls(state, onIntent)
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                ActiveQueueList(state = state, onIntent = onIntent)
                            }
                        }
                    } else {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .verticalScroll(rememberScrollState())
                        ) {
                            StaffControls(
                                state = state,
                                onIntent = onIntent,
                                modifier = Modifier.fillMaxWidth()
                            )
                            ActiveQueueList(
                                state = state,
                                onIntent = onIntent,
                                modifier = Modifier.fillMaxWidth()
                            )
                        }
                    }
                }
            }
        }
    }

    if (showQrDialog) {
        val shopId = state.shop?.id ?: "shop_001"
        val customerUrl = "https://qfast.cmdevstudio.tech/?shop=$shopId"

        AlertDialog(
            onDismissRequest = { showQrDialog = false },
            title = { Text(text = "QR Code สำหรับลูกค้า") },
            text = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "สแกนเพื่อรับบัตรคิวและติดตามสถานะ",
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Image(
                        painter = rememberQrCodePainter(customerUrl),
                        contentDescription = "Shop QR Code",
                        modifier = Modifier
                            .size(200.dp)
                            .background(Color.White)
                            .padding(8.dp)
                    )
                }
            },
            confirmButton = {
                TextButton(onClick = { showQrDialog = false }) {
                    Text("ปิด")
                }
            }
        )
    }

    state.newlyIssuedTicket?.let { ticket ->
        val shopId = state.shop?.id ?: "shop_001"
        val customerUrl = "https://qfast.cmdevstudio.tech/?shop=$shopId&q=${ticket.category}${ticket.number}"

        AlertDialog(
            onDismissRequest = { onIntent(StaffContract.Intent.DismissTicketDialog) },
            title = { 
                Text(
                    text = "ออกคิวสำเร็จ",
                    modifier = Modifier.fillMaxWidth(),
                    textAlign = TextAlign.Center,
                    fontWeight = FontWeight.Bold
                ) 
            },
            text = {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "${ticket.category}${ticket.number}",
                        fontSize = 48.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = MaterialTheme.colorScheme.primary
                    )
                    Text(
                        text = "ให้ลูกค้าสแกนเพื่อรับคิวของตนเอง",
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Image(
                        painter = rememberQrCodePainter(customerUrl),
                        contentDescription = "Ticket QR Code",
                        modifier = Modifier
                            .size(250.dp)
                            .background(Color.White)
                            .padding(8.dp)
                    )
                }
            },
            confirmButton = {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedButton(
                        onClick = { onIntent(StaffContract.Intent.PrintSpecificTicket(ticket)) },
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("พิมพ์ (Print)")
                    }
                    Button(
                        onClick = { onIntent(StaffContract.Intent.DismissTicketDialog) },
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("ปิด (Close)")
                    }
                }
            }
        )
    }
}

@Composable
private fun StaffControls(
    state: StaffContract.State,
    onIntent: (StaffContract.Intent) -> Unit,
    modifier: Modifier = Modifier
) {
    val isShopActive = state.shop?.isActive == true
    val palette = listOf(
        Color(0xFFE3F2FD), Color(0xFFE8F5E9), Color(0xFFFFF3E0),
        Color(0xFFF3E5F5), Color(0xFFE0F7FA), Color(0xFFFCE4EC),
        Color(0xFFFFFDE7), Color(0xFFEFEBE9)
    )
    val activeCategories = state.shop?.categories?.ifEmpty { null }
        ?: mapOf("A" to "1-2 คน", "B" to "2-3 คน", "C" to "3-4 คน", "D" to "5 คนขึ้นไป")
    val categories = activeCategories.entries.sortedBy { it.key }.mapIndexed { index, entry ->
        Triple(entry.key, entry.value, palette[index % palette.size])
    }

    Column(
        modifier = modifier.fillMaxWidth().padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Text(
                text = state.shop?.name ?: "Loading...",
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.weight(1f, fill = false),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            if (state.shop != null) {
                SuggestionChip(
                    onClick = {},
                    label = { Text(if (isShopActive) "เปิดร้าน" else "ปิดร้าน", fontWeight = FontWeight.Bold) },
                    colors = SuggestionChipDefaults.suggestionChipColors(
                        containerColor = if (isShopActive) Color(0xFFE2F0D9) else Color(0xFFFFDAD9),
                        labelColor = if (isShopActive) Color(0xFF385723) else Color(0xFFBA1A1A)
                    ),
                    border = null
                )
            }
        }

        if (state.shop != null) {
            val lastTicketObj = state.queues.maxByOrNull { it.number }
            val lastTicketDisplay = if (lastTicketObj != null) {
                "${lastTicketObj.category}${lastTicketObj.number}"
            } else if (state.shop.lastTicket > 0) {
                "#${state.shop.lastTicket}"
            } else {
                "-"
            }
            Text(
                text = "ออกคิวล่าสุด: $lastTicketDisplay",
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.ExtraBold,
                color = MaterialTheme.colorScheme.primary,
                modifier = Modifier.padding(horizontal = 4.dp)
            )
        }

        OutlinedButton(
            onClick = { onIntent(StaffContract.Intent.ResetQueue) },
            modifier = Modifier.fillMaxWidth(),
            colors = ButtonDefaults.outlinedButtonColors(
                contentColor = MaterialTheme.colorScheme.error
            ),
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.error),
            shape = MaterialTheme.shapes.large
        ) {
            Icon(Icons.Default.DeleteForever, contentDescription = null)
            Spacer(modifier = Modifier.width(8.dp))
            Text("ล้างคิวประจำวัน (Reset Queue)", fontWeight = FontWeight.Bold)
        }

        categories.forEach { (cat, label, color) ->
            val currentServing = state.shop?.currentServings?.get(cat) ?: 0
            val hasWaiting = state.queues.any { it.category == cat && it.status == "waiting" }

            FloatingCard(
                modifier = Modifier.fillMaxWidth(),
                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().background(color.copy(alpha = 0.2f)).padding(12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(text = "หมวด $cat: $label", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleMedium)
                        Text(
                            text = if (currentServing > 0) "กำลังเรียก: $cat$currentServing" else "กำลังเรียก: -",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        IconButton(
                            onClick = { onIntent(StaffContract.Intent.NextQueue(cat)) },
                            enabled = isShopActive && hasWaiting,
                            colors = IconButtonDefaults.iconButtonColors(
                                containerColor = MaterialTheme.colorScheme.primary,
                                contentColor = MaterialTheme.colorScheme.onPrimary
                            ),
                            modifier = Modifier.size(40.dp)
                        ) {
                            Icon(Icons.Default.SkipNext, contentDescription = "Next $cat")
                        }

                        IconButton(
                            onClick = { onIntent(StaffContract.Intent.IssueTicket(cat)) },
                            enabled = isShopActive,
                            colors = IconButtonDefaults.iconButtonColors(
                                containerColor = MaterialTheme.colorScheme.secondaryContainer,
                                contentColor = MaterialTheme.colorScheme.onSecondaryContainer
                            ),
                            modifier = Modifier.size(40.dp)
                        ) {
                            Icon(Icons.Default.Add, contentDescription = "Issue $cat")
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ActiveQueueList(
    state: StaffContract.State,
    onIntent: (StaffContract.Intent) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier.fillMaxWidth().padding(16.dp)) {
        Text(
            text = "Active Queue",
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold,
            modifier = Modifier.padding(bottom = 16.dp)
        )
        Column(
            modifier = Modifier.fillMaxWidth().animateContentSize(),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            state.queues.sortedBy { it.number }.forEach { ticket ->
                val shopId = state.shop?.id ?: return@forEach
                val isServing = ticket.status == "serving"
                val cardBorder = if (isServing) {
                    BorderStroke(3.dp, MaterialTheme.colorScheme.primary.copy(alpha = 0.8f))
                } else null

                AnimatedVisibility(
                    visible = true,
                    enter = fadeIn() + expandVertically(),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    FloatingCard(
                        modifier = Modifier.fillMaxWidth().then(
                            if (isServing) Modifier.graphicsLayer(scaleX = 1.03f, scaleY = 1.03f) else Modifier
                        ).clickable { onIntent(StaffContract.Intent.SelectTicketForQr(ticket)) },
                        border = cardBorder
                    ) {
                        Column(modifier = Modifier.fillMaxWidth().padding(12.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Column {
                                    val tableLabel = state.shop?.categories?.get(ticket.category) ?: ""
                                    val detailText = if (tableLabel.isNotBlank()) "หมวด ${ticket.category} ($tableLabel)" else "หมวด ${ticket.category}"
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Text(
                                            text = "คิว ${ticket.category}${ticket.number}",
                                            style = MaterialTheme.typography.titleLarge,
                                            fontWeight = FontWeight.ExtraBold
                                        )
                                        if (isServing) {
                                            Spacer(modifier = Modifier.width(8.dp))
                                            SuggestionChip(
                                                onClick = {},
                                                label = {
                                                    Text(
                                                        "กำลังเรียก",
                                                        fontWeight = FontWeight.Bold
                                                    )
                                                },
                                                colors = SuggestionChipDefaults.suggestionChipColors(
                                                    containerColor = MaterialTheme.colorScheme.primaryContainer,
                                                    labelColor = MaterialTheme.colorScheme.primary
                                                )
                                            )
                                        }
                                    }
                                    Text(
                                        text = detailText,
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                                Column(horizontalAlignment = Alignment.End) {
                                    Text(
                                        text = "เวลา: ${ticket.time}",
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                    if (!ticket.callTime.isNullOrBlank() && ticket.callTime != "<null>" && ticket.callTime != "null") {
                                        Text(
                                            text = "เรียกเมื่อ: ${ticket.callTime}",
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = MaterialTheme.colorScheme.primary,
                                            fontWeight = FontWeight.Bold
                                        )
                                    }
                                }
                            }

                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.End,
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                IconButton(onClick = {
                                    onIntent(StaffContract.Intent.SelectTicketForQr(ticket))
                                }) {
                                    Icon(Icons.Default.QrCode, contentDescription = "Show QR")
                                }
                                if (ticket.status == "serving") {
                                    IconButton(onClick = {
                                        onIntent(
                                            StaffContract.Intent.RecallQueue(
                                                shopId,
                                                ticket.number
                                            )
                                        )
                                    }) {
                                        Icon(Icons.Default.VolumeUp, contentDescription = "Recall")
                                    }
                                    IconButton(onClick = {
                                        onIntent(
                                            StaffContract.Intent.CompleteQueue(
                                                shopId,
                                                ticket.number
                                            )
                                        )
                                    }) {
                                        Icon(Icons.Default.Check, contentDescription = "Complete")
                                    }
                                }
                                if (ticket.status == "waiting") {
                                    IconButton(onClick = {
                                        onIntent(
                                            StaffContract.Intent.SkipQueue(
                                                shopId,
                                                ticket.number
                                            )
                                        )
                                    }) {
                                        Icon(Icons.Default.Close, contentDescription = "Skip")
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
